import {
  add,
  differenceInCalendarDays,
  differenceInWeeks,
  formatDistanceStrict,
  formatDuration,
  intervalToDuration,
} from "date-fns";

const BLOCKS_PER_HOUR = 6;
const WEEKS_PRECISION = 5;
const DAY_TO_WEEK_DISPLAY_THRESHOLD = 30;

/**
 * Converts a number of blocks into days or weeks
 * Returns the time in days if the difference is less than 7 days
 * Otherwise, returns the time in weeks
 *
 * @param {number | undefined} blocks - The number of blocks to convert.
 * @returns {string} - The converted time in days or weeks.
 * Rounded to 5 weeks if the difference is greater than 7 days.
 *
 * Examples of usage:
 * blocksToDisplayTime(30000); // "29 weeks"
 * blocksToDisplayTime(1); // "1 day"
 * blocksToDisplayTime(200); // "2 days"
 */
export const blocksToDisplayTime = (blocks: number | undefined): string => {
  // If no blocks are provided, return default value
  if (!blocks) return "-";

  // Calculate the equivalent time in hours
  const hours = blocks / BLOCKS_PER_HOUR;

  // Calculate the start and end dates
  // get the timestamp now
  const startDate = new Date();
  const endDate = add(startDate, { hours });

  const dayDifference = differenceInCalendarDays(endDate, startDate);
  // If the difference is greater than or equal to 30 days, return the difference in weeks
  if (dayDifference >= DAY_TO_WEEK_DISPLAY_THRESHOLD) {
    // Calculate the difference in weeks
    const weeks = differenceInWeeks(endDate, startDate, {
      roundingMethod: "ceil",
    });
    const roundedWeeks = Math.round(weeks / WEEKS_PRECISION) * WEEKS_PRECISION;
    return `~${roundedWeeks} weeks`;
  }

  // Hybrid day formatting under 30 days:
  // - Use formatDistanceStrict with roundingMethod 'round' to get nearest day count
  // - If under 1 day, display '< 1 day'
  // - If not an exact integer number of days, prefix with '~'
  const daysDecimal = hours / 24;
  if (daysDecimal < 1) {
    return "< 1 day";
  }

  const roundedLabel = formatDistanceStrict(startDate, endDate, {
    unit: "day",
    roundingMethod: "round",
  });
  const roundedDays = Number.parseInt(roundedLabel, 10);
  const isInteger = Math.abs(daysDecimal - Math.round(daysDecimal)) < 1e-9;
  if (isInteger) {
    return `${roundedDays} ${roundedDays === 1 ? "day" : "days"}`;
  }
  return `~${roundedDays} ${roundedDays === 1 ? "day" : "days"}`;
};

interface Duration {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

/**
 * Returns the duration between the start timestamp and the current time
 *
 * @param {string} startTimestamp - The start timestamp.
 * @param {number} currentTime - The current time.
 * @returns {string} - The duration between the start timestamp and the current time.
 */
export const durationTillNow = (
  startTimestamp: string,
  currentTime: number,
  isPrecise: boolean = true,
) => {
  if (!startTimestamp || startTimestamp.startsWith("000")) return "Ongoing";

  // Coarse formatting branch when precision is not required
  if (!isPrecise) {
    const startMs = new Date(startTimestamp).getTime();
    const diffMs = Math.max(0, currentTime - startMs);

    // Less than a minute
    if (diffMs < 60 * 1000) return "Just now";

    // Under an hour: show minutes
    if (diffMs < 60 * 60 * 1000) {
      const minutes = Math.floor(diffMs / (60 * 1000));
      return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
    }

    // Under a day: show hours
    if (diffMs < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(diffMs / (60 * 60 * 1000));
      return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    }

    // 1 day or more: show days
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  }

  const duration = intervalToDuration({
    end: currentTime,
    start: new Date(startTimestamp),
  });

  let format: (keyof Duration)[] = [];

  // If there are months or years, only show months and days
  if (duration.months || duration.years) {
    format = ["years", "months", "days"];
  }
  // If only days or less, show more detailed time
  else {
    format = ["days", "hours", "minutes"];
    // Add seconds only if less than a minute
    if (!duration.days && !duration.hours && !duration.minutes) {
      format.push("seconds");
    }
  }

  const formattedTime = formatDuration(duration, {
    format,
  });
  if (formattedTime) {
    return `${formattedTime} ago`;
  } else {
    return "Just now";
  }
};

/**
 * Formats the time remaining until a future completion time
 *
 * @param {string} completionTime - The completion timestamp (ISO string).
 * @returns {string} - The formatted time remaining (e.g., "2 hours 30 minutes" or "45 minutes").
 */
export const formatTimeRemaining = (completionTime: string): string => {
  const now = new Date();
  const completion = new Date(completionTime);

  if (completion <= now) {
    return "0 minutes";
  }

  const duration = intervalToDuration({
    start: now,
    end: completion,
  });

  const formatted = formatDuration(duration, {
    format: ["hours", "minutes"],
  });
  return formatted === "" ? "< 1 minute" : formatted;
};
